import React, { useState, useEffect, useRef } from 'react';
import TextInput from '../atoms/TextInput';
import Button from '../atoms/Button';
import { useChat } from '../../context/ChatContext';
import './ChatFooter.css';

const ChatFooter = () => {
  const [message, setMessage] = useState('');
  const [recordingTime, setRecordingTime] = useState(0);
  const recordingTimerRef = useRef(null);
  
  const { 
    sendMessage, 
    isLoading, 
    isRecording, 
    isSpeaking,
    startVoiceRecording, 
    stopVoiceRecording,
    transcript,
    browserSupportsSpeechRecognition,
    isMicrophoneAvailable,
    // Mobile Safari TTS support
    pendingResponse,
    playPendingResponse,
    isMobileSafari
  } = useChat();

  // Handle recording timer
  useEffect(() => {
    if (isRecording) {
      // Start a timer to track recording duration
      setRecordingTime(0);
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } else {
      // Clear the timer when recording stops
      clearInterval(recordingTimerRef.current);
      setRecordingTime(0);
    }
    
    // Cleanup on unmount
    return () => {
      clearInterval(recordingTimerRef.current);
    };
  }, [isRecording]);

  // Format recording time as minutes:seconds
  const formatRecordingTime = () => {
    const minutes = Math.floor(recordingTime / 60);
    const seconds = recordingTime % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (message.trim() && !isLoading && !isSpeaking) {
      sendMessage(message);
      setMessage('');
    }
  };

  const handleVoiceButton = () => {
    if (isRecording) {
      stopVoiceRecording();
    } else {
      startVoiceRecording();
    }
  };

  // Prevent form submission while recording
  const onKeyDown = (e) => {
    if (e.key === 'Enter' && isRecording) {
      e.preventDefault();
      stopVoiceRecording();
    }
  };

  // Show speech recognition support status
  const getPlaceholderText = () => {
    if (!browserSupportsSpeechRecognition) {
      return "Speech recognition not supported in this browser";
    }
    
    if (!isMicrophoneAvailable) {
      return "Microphone access required";
    }
    
    if (isRecording) {
      return `Recording${recordingTime > 0 ? ` ${formatRecordingTime()}` : ''}... (click mic to stop)`;
    }
    
    return "Type a message...";
  };

  return (
    <div className="chat-footer">
      {/* Mobile Safari play button for pending response */}
      {isMobileSafari && pendingResponse && (
        <div className="mobile-safari-play-notification">
          <span className="play-notification-text">🔊 Tap to play teacher's response</span>
          <Button 
            variant="primary" 
            size="small" 
            onClick={playPendingResponse}
            className="play-response-button"
          >
            ▶️ Play
          </Button>
        </div>
      )}
      
      {/* Real-time transcript display */}
      {isRecording && transcript && (
        <div className="transcript-preview">
          <span className="transcript-label">Live Transcript:</span>
          <span className="transcript-text">{transcript}</span>
        </div>
      )}
      
      <form className="chat-footer-form" onSubmit={handleSubmit}>
        <Button 
          type="button"
          variant="outline" 
          size="small" 
          className={`chat-footer-button ${isRecording ? 'recording' : ''}`}
          onClick={handleVoiceButton}
          disabled={isSpeaking || !browserSupportsSpeechRecognition || !isMicrophoneAvailable}
          aria-label={isRecording ? "Stop recording" : "Start recording"}
          title={isRecording ? "Stop recording" : "Start recording"}
        >
          <span className="footer-icon">{isRecording ? '⏹️' : '🎤'}</span>
        </Button>
        <TextInput
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder={getPlaceholderText()}
          className="chat-footer-input"
          disabled={isRecording || isSpeaking}
          onKeyDown={onKeyDown}
        />
        <Button 
          type="submit" 
          variant="primary" 
          size="small"
          className="chat-footer-send"
          disabled={!message.trim() || isLoading || isSpeaking || isRecording}
        >
          <span className="footer-icon">➤</span>
        </Button>
      </form>
    </div>
  );
};

export default ChatFooter; 